import { useState, useCallback, useEffect } from 'react';
import { ApiActionError } from '@/utils/errors';
import { useSetAtom } from 'jotai';
import { alertModalAtom, confirmModalAtom } from '@/atom/modal';
import { useRouter } from 'next/navigation';
import { useErrorBoundary } from 'react-error-boundary';
import { redirectToZzimSignin } from '@/utils';

interface UseApiActionOptions<T> {
  initialData?: T | null;
}

type ApiErrorObject = Omit<ApiActionError, 'originalError'>;

interface UseApiActionResult<T, Args extends any[]> {
  execute: (...args: Args) => Promise<void>;
  data: T | null;
  isLoading: boolean;
  error: ApiErrorObject | null;
  reset: () => void;
}

interface UseActionHandlerConfig<T, Args extends any[]> {
  action: (...args: Args) => Promise<{ data: T | null; error: ApiErrorObject | null }>;
  options?: UseApiActionOptions<T>;
  key: string | number;
}

export default function useActionHandler<T, Args extends any[]>({
  action,
  options,
  key,
}: UseActionHandlerConfig<T, Args>): UseApiActionResult<T, Args> {
  const [data, setData] = useState<T | null>(options?.initialData ?? null);
  const [error, setError] = useState<ApiErrorObject | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const setAlertModal = useSetAtom(alertModalAtom);
  const router = useRouter();
  const { showBoundary } = useErrorBoundary();
  const setConfirmModal = useSetAtom(confirmModalAtom);

  useEffect(() => {
    setData(options?.initialData ?? null);
    setError(null);
    setIsLoading(false);
  }, [key]);

  const execute = useCallback(
    async (...args: Args): Promise<void> => {
      setIsLoading(true);
      setError(null);
      const result = await action(...args);

      if (result.error) {
        const errorCategory = result.error.category;

        setError(result.error);
        setData(null);

        if (errorCategory === 'CUSTOM') {
          setIsLoading(false);
          return;
        }

        if (errorCategory === 'AUTH') {
          setConfirmModal({
            visible: true,
            title: '로그인이 만료되었습니다',
            message: '다시 로그인해주세요.',
            onConfirm: () => {
              setConfirmModal({ visible: false });
              redirectToZzimSignin();
            },
            onClose: () => {
              setConfirmModal({ visible: false });
            },
          });
        }

        if (errorCategory === 'ALERT') {
          setAlertModal({
            visible: true,
            title: '알림',
            message: result.error.message,
            onConfirm: () => {
              router.back();
              setAlertModal({ visible: false });
            },
          });
          setIsLoading(false);
          return;
        }

        if (errorCategory === 'ERROR_BOUNDARY') {
          showBoundary(error);
          setIsLoading(false);
        }
      } else {
        setData(result.data as T);
        setError(null);
      }
      setIsLoading(false);
    },
    [action, setAlertModal, router],
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return { execute, data, isLoading, error, reset };
}
